import axios from 'axios';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API

// Function to use Google Geolocation API as a fallback
async function fetchGoogleCoordinates() {
  try {
    const response = await axios.post(
      `https://www.googleapis.com/geolocation/v1/geolocate?key=${GOOGLE_API_KEY}`
    );
    const { lat, lng } = response.data.location;
    return [lng, lat];
  } catch (error) {
    console.error('Google Geolocation API error:', error);
    return null;
  }
}

/**
 * Resolves the user's coordinates with a tagged source so callers
 * can distinguish a real signal from the silent NYC fallback.
 *
 * Returns: { coordinates: [lng, lat], source }
 *   source ∈ {
 *     'gps'      — browser navigator.geolocation succeeded,
 *     'ip'       — Google IP-based geolocation succeeded,
 *     'fallback' — both above failed, returning the NYC default.
 *   }
 *
 * Features that should NOT pretend the user is in NYC when geo
 * fails (notably AI search) check `source === 'fallback'` and
 * degrade gracefully instead of running with a wrong location.
 * Other features keep using the NYC value so they keep working.
 *
 * The function still resolves with a coordinates array in all
 * three cases so existing destructuring (`[lng, lat] = ...`) on
 * the `coordinates` field keeps working.
 */
export async function getUserCoordinates() {
  const fallbackCoordinates = [-74.006, 40.7128]; // NYC fallback

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        resolve({ coordinates: [longitude, latitude], source: 'gps' });
      },
      async (error) => {
        console.warn('Browser Geolocation error:', error.message);
        const googleCoordinates = await fetchGoogleCoordinates();
        if (googleCoordinates) {
          resolve({ coordinates: googleCoordinates, source: 'ip' });
        } else {
          resolve({ coordinates: fallbackCoordinates, source: 'fallback' });
        }
      },
    );
  });
}

export async function reverseGeocode(lat, lng) {
  try {
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
    );

    if (response.data.results.length > 0) {
      const priorityTypes = ["locality", "administrative_area_level_3", "administrative_area_level_1"];

      for (const type of priorityTypes) {
        const result = response.data.results.find(res => res.types.includes(type));
        if (result) {
          const parts = result.formatted_address.split(", ");

          return parts.length >= 3 ? parts.slice(-3).join(", ") : result.formatted_address;
        }
      }
    }

    return "Unknown Location";
  } catch (error) {
    console.error("❌ Reverse Geocoding Error:", error);
    return "Unknown Location";
  }
}

// Function to check if location access is blocked
export function checkLocationAccess(setLocationBlocked) {
  navigator.geolocation.getCurrentPosition(
    () => {
      setLocationBlocked(false); // Location access is allowed
    },
    (error) => {
      if (error.code === error.PERMISSION_DENIED) {
        setLocationBlocked(true); // Location access is blocked
      }
    }
  );
}